<!-- eslint-disable vue/no-static-inline-styles -->
<template>
  <div
    class="nosto_cart"
    style="display: none"
  >
    <div
      v-for="item of nostoCartItems"
      :key="item.id"
      class="line_item"
    >
      <span class="product_id">{{ item.product_id }}</span>
      <span class="sku_id">{{ item.sku_id }}</span>
      <span class="quantity">{{ item.quantity }}</span>
      <span class="name">{{ item.name }}</span>
      <span class="unit_price">{{ item.unit_price }}</span>
      <span class="price_currency_code">{{ item.price_currency_code }}</span>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-unexpected-multiline, vue/multi-word-component-names */

/**
 * Vue: Nosto
 * -----------------------------------------------------------------------------
 * Global API handler for Nosto recommendations.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import { formatNostoProduct } from '~/helpers/format-nosto'
import { waitFor } from '~/helpers/general'

export default {
  name: 'Nosto',

  data() {
    return {
      method: 'viewOther',
      methodParam: false,
      state: 'waiting',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      hydrated: (state) => state.cart.hydrated,
      nostoMethod: (state) => state.nosto.method,
      nostoMethodParam: (state) => state.nosto.methodParam,
      nostoPlacements: (state) => state.nosto.placements,
      nostoReady: (state) => state.nosto.ready,
    }),

    /**
     * Compute Nosto formatted cart items.
     * @returns {Array}
     */
    nostoCartItems() {
      return this.cart.items.map((item) => {
        return {
          name: item.title,
          price_currency_code: cnvs.store.currency,
          product_id: String(item.product_id),
          quantity: item.quantity,
          sku_id: String(item.sku),
          unit_price: parseFloat(Number(item.original_price / 100).toFixed(2)),
        }
      })
    },
  },

  watch: {

    /**
     * Watch placements to load from Nosto when changed.
     */
    nostoPlacements() {
      if (!this.nostoReady) {
        return
      }

      this.handlePlacementsChange()
    },

    cart() {
      this.handleCartChange()
    },
  },

  mounted() {
    this.getMethod()

    waitFor('nostojs', () => {
      nostojs((api) => api.setAutoLoad(false))
      this.setNostoReady()
      this.state = 'ready'

      this.handleCollectionClickLog()
      this.handleProductClickLog()

      if (!this.nostoPlacements.length) {
        return
      }

      this.handlePlacementsChange()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setMethod: 'nosto/setMethod',
      setMethodParam: 'nosto/setMethodParam',
      setNostoReady: 'nosto/setReady',
      setPlacements: 'nosto/setPlacements',
      setRecommendations: 'nosto/setRecommendations',
    }),

    /**
     * Get method type and parameter.
     * - Method type and parameter depend on the page type.
     * - Used to change what nostojs function is run.
     * - Set to Vuex store.
     */
    getMethod() {
      switch (cnvs.page.type) {
        case 'cart':
          this.method = 'viewCart'
          break
        case 'collection':
          this.method = 'viewCategory'
          this.methodParam = cnvs.nosto.collectionTitle
          break
        case 'index':
          this.method = 'viewFrontPage'
          break
        case 'product':
          this.method = 'viewProduct'
          this.methodParam = cnvs.nosto.productId
          break
        case 'search':
          this.method = 'viewSearch'
          this.methodParam = cnvs.nosto.searchTerms
          break
      }

      this.setMethod(this.method)
      this.setMethodParam(this.methodParam)
    },

    /**
     * Set placements before proceed any action.
     *
     * @param {Object} api - NostoApi instance.
     * @param {Object} action - Lazysizes event.
     */
    injectPlacements(api, action) {
      action.setPlacements(api.placements.getPlacements())
        .load()
        .then(response => api.placements.injectCampaigns(response.campaigns.content))
    },

    /**
     * Handle placements change.
     */
    async handlePlacementsChange() {
      if (this.state !== 'ready' || !this.nostoPlacements.length) {
        return
      }

      this.state = 'loading'
      let response
      let recommendations

      /**
       * Fetch recommendations from Nosto.
       */
      try {
        response = await this.queryPlacements({
          method: this.nostoMethod,
          methodParam: this.nostoMethodParam,
          placements: this.nostoPlacements,
        })
      } catch (error) {
        reportError('Failed to fetch Nosto recommendations', error)
      }

      /**
       * Format recommendations.
       */
      try {
        recommendations = this.formatRecommendations(response)
      } catch (error) {
        reportError('Failed to format Nosto recommendations', error)
      }

      await this.setRecommendations(recommendations)
      await this.setPlacements(false)
      this.state = 'ready'
    },

    /**
     * Query placement IDs.
     * @param {Object} data - Data object.
     * @param {Array} placements - Array of Nosto placement IDs.
     * @param {String} [method] - Type of request, e.g. 'viewFrontPage'.
     * @param {String|Number} [methodParam] - Collection title or product ID to
     * refine recommended products to.
     * @returns {Promise}
     */
    queryPlacements({ method, methodParam, placements }) {
      return new Promise((resolve, reject) => {
        nostojs((api) => {
          api
            .defaultSession()
            .setCart({ items: this.nostoCartItems })
            [method](methodParam)
            .setPlacements(placements)
            .load()
            .then(resolve)
            .catch(reject)
        })
      })
    },

    /**
     * Format Nosto recommendations.
     * @param {Object} response - Nosto response.
     * @returns {Object}
     */
    formatRecommendations(response) {
      const { recommendations } = response
      const formattedRecommendations = {}

      /**
       * Format response.
       * - Prevent 'example.com' URLs from breaking Nosto when `nostodebug=true`
       *   is set in URL.
       */
      Object.entries(recommendations).forEach(([id, value]) => {
        const products = value.products.map((product) => {
          if (!product.url.includes('/products/')) {
            return false
          }

          return formatNostoProduct(product)
        }).filter(Boolean)

        formattedRecommendations[id] = {
          id,
          response: true,
          resultId: value.result_id,
          products,
        }
      })

      /**
       * Create empty objects for placements if response didn't contain them.
       */
      this.nostoPlacements.forEach((id) => {
        if (formattedRecommendations[id]) {
          return
        }

        formattedRecommendations[id] = {
          id,
          response: false,
          resultId: false,
          products: [],
        }
      })

      return formattedRecommendations
    },

    /**
     * Handle collection click log for nosto. See more;
     * https://docs.nosto.com/techdocs/apis/frontend/implementation-guide-session-api/spa-basics-tracking-events#upon-viewing-a-collection
     */
    handleCollectionClickLog() {
      if (cnvs.page.type !== 'collection') {
        return
      }

      try {
        const collectionTitle = cnvs.nosto.collectionTitle
        nostojs(api => {
          this.injectPlacements(
            api,
            api.defaultSession().viewCategory(collectionTitle),
          )
        })

      } catch (error) {
        reportError('Failed to log Nosto collection click', error)
      }
    },

    /**
     * Handle product click log for nosto. See more;
     * https://docs.nosto.com/techdocs/apis/frontend/implementation-guide-session-api/spa-basics-leveraging-features#handling-attribution
     */
    handleProductClickLog() {
      if (cnvs.page.type !== 'product') {
        return
      }

      try {
        const productId = cnvs.nosto.productId
        nostojs(api => {
          this.injectPlacements(
            api,
            api.defaultSession().viewProduct(productId),
          )
        })

      } catch (error) {
        reportError('Failed to log Nosto product click', error)
      }
    },

    /**
     * Feed nosto API with the updated cart state.
     */
    async handleCartChange() {
      if (this.hydrated || !window.nostojs) {
        return
      }

      await new Promise((resolve) => {
        nextTick().then(resolve)
      })

      try {
        waitFor('nostojs', () => {
          nostojs((api) => api.resendCartTagging())
        })

      } catch (error) {
        reportError('Failed to update Nosto cart', error)
      }
    },
  },
}
</script>
